import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

const NEWS_BATCH_SIZE = 5;

const LatestNews = ({ news, playerInfo, twoColumn = false, label = null }) => {
  const [visibleCount, setVisibleCount] = useState(NEWS_BATCH_SIZE);
  const containerRef = useRef(null);

  useEffect(() => {
    setVisibleCount(NEWS_BATCH_SIZE); // Reset if news (or latestNews) changes
  }, [news, playerInfo]);

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;
      const effective = getEffectiveNews();
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
        // Near bottom
        setVisibleCount((prev) => Math.min(prev + NEWS_BATCH_SIZE, effective.length));
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [news, playerInfo]);

  // Build the effective list: use AggregatedNews (news prop) if available, otherwise fallback to latestNews from player payload
  const getEffectiveNews = () => {
    let info = playerInfo;
    if (typeof info === 'string') {
      try { info = JSON.parse(info); } catch { info = playerInfo; }
    }
    const fallback = (info && info.latestNews) || [];
    return (news && news.length > 0) ? news : fallback;
  };

  const effectiveNews = getEffectiveNews();
  const visibleNews = effectiveNews.slice(0, visibleCount);
  const isAggregated = news && news.length > 0;

  // Format date helper: if within 24 hours -> 'Xh Xm ago', otherwise -> locale date
  const formatDateOrAgo = (input) => {
    if (!input) return '';
    const d = new Date(input);
    if (isNaN(d)) return '';
    const now = new Date();
    const diffMs = now - d;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };
 
  return (
    <div
      className="news-column aggregated"
      ref={containerRef}
      style={{ maxHeight: 500, overflowY: 'auto' }}
    >
      {/* Debug info for UI */}
      {/* Compute leagueFromJSON once and use everywhere */}
      {(() => {
        let leagueFromJSON = '';
        try {
          const info = typeof playerInfo === 'string' ? JSON.parse(playerInfo) : playerInfo;
          if (info && Array.isArray(info.playerInfo)) {
            const sid = info.playerInfo[0]?.sportsID;
            if (sid === 1) leagueFromJSON = 'NFL';
            else if (sid === 2) leagueFromJSON = 'NBA';
            else if (sid === 3) leagueFromJSON = info.playerInfo[0]?.league;
            else if (sid === 5) leagueFromJSON = 'CFB';
            else leagueFromJSON = info.playerInfo[0]?.league;
          } else if (info && typeof info === 'object') {
            leagueFromJSON = info.league;
          }
        } catch { }
        return <>
          <div className="news-header" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <svg fill="#000000" width="40px" height="40px" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path d="M 5 5 L 5 9 C 14.93 9 23 17.07 23 27 L 27 27 C 27 14.85 17.15 5 5 5 z M 5 12 L 5 16 C 11.07 16 16 20.93 16 27 L 20 27 C 20 18.72 13.28 12 5 12 z M 8 21 A 3 3 0 0 0 8 27 A 3 3 0 0 0 8 21 z" /></svg>
            <h2 className="aggregated-news-title">
              {label || (() => {
                const info = typeof playerInfo === 'string' ? JSON.parse(playerInfo) : playerInfo;
                const sid = info?.playerInfo?.[0]?.sportsID;
                if (sid === 3 || sid === 5) return 'Latest Player News';
                return `Latest ${leagueFromJSON || 'NFL'} News`;
              })()}
            </h2>
          </div>
        </>;
      })()}

      {effectiveNews.length === 0 ? (
        <p>No news found.</p>
      ) : (
        twoColumn ? (
          // twoColumn=true means both Twitter and Aggregated are empty -> show two-column grid
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {visibleNews.map((item, idx) => (
              <div key={idx}>
                <div className="news-post">
                  <div className="news-body">
                    <div className="post-header">
                      <p>
                        <strong className="username">{item.Source || item.source || 'Sports News'}</strong>{' '}
                        <span className="postdate">{formatDateOrAgo(item.Updated || item.updated)}</span>
                      </p>
                      <p className="subheading"><strong>{item.Title || item.title}</strong></p>
                    </div>
                    <div className="post-body">
                      {(item.ImageUrl || item.image_url || item.imageUrl) && <img className="postimg" src={item.ImageUrl || item.image_url || item.imageUrl} alt="News" />}
                      <p>{item.Content || item.content}</p>
                      {(item.Url || item.url) && (
                        <a href={item.Url || item.url} target="_blank" rel="noopener noreferrer" className="read-more-link">Read more →</a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Otherwise, show single-column list alongside other widgets
          visibleNews.map((item, idx) => (
            <div className="news-post" key={idx}>
              <div className="news-body">
                <div className="post-header">
                  <p>
                    <strong className="username">{item.Source || item.source || 'Sports News'}</strong>{' '}
                    <span className="postdate">{formatDateOrAgo(item.Updated || item.updated)}</span>
                  </p>
                  <p className="subheading"><strong>{item.Title || item.title}</strong></p>
                </div>
                <div className="post-body">
                  {(item.ImageUrl || item.image_url || item.imageUrl) && <img className="postimg" src={item.ImageUrl || item.image_url || item.imageUrl} alt="News" />}
                  <p>{item.Content || item.content}</p>
                  {(item.Url || item.url) && (
                    <a href={item.Url || item.url} target="_blank" rel="noopener noreferrer" className="read-more-link">Read more →</a>
                  )}
                </div>
              </div>
            </div>
          ))
        )
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  playerInfo: state.player && state.player.data ? state.player.data : null
});

export default connect(mapStateToProps)(LatestNews);
